import React, { useEffect } from 'react';
import { 
  View, 
  StyleSheet, 
  Text, 
  ImageBackground, 
  SafeAreaView, 
  StatusBar, 
  Platform, 
  TouchableOpacity,
  Linking,
  ScrollView,
  Image
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import colors from '../theme/colors';
import { logEvent } from '../utils/firebase';

const LandingScreen = ({ navigation }) => {
  useEffect(() => {
    logEvent('screen_view', { screen_name: 'Landing' });
  }, []);

  const handleCardPress = (cardName, action) => {
    logEvent('landing_card_clicked', { card_name: cardName });
    action();
  };

  const cards = [
    {
      id: 1,
      title: 'Learning Packs',
      subtitle: 'Master the dialect',
      icon: 'book',
      backgroundColor: '#d4a574',
      action: () => navigation.navigate('LearningPacks')
    },
    {
      id: 2,
      title: 'M3arab Guide',
      subtitle: 'Text like a local',
      icon: 'language',
      backgroundColor: '#a88c76',
      action: () => Linking.openURL('https://m3arab.kuwaitiwords.com/')
    },
    {
      id: 3,
      title: 'Map of Kuwait',
      subtitle: 'Discover Kuwait',
      icon: 'map',
      backgroundColor: '#c9a57a',
      action: () => Linking.openURL('https://maps.kuwaitiwords.com/')
    },
    {
      id: 4,
      title: 'Words & Proverbs',
      subtitle: 'Cultural wisdom',
      icon: 'chatbubbles',
      backgroundColor: '#b89268',
      action: () => navigation.navigate('Words')
    }
  ];

  return (
    <ImageBackground 
      source={require('../../assets/light-bg.jpg')} 
      style={styles.backgroundImage}
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" />
        
        {/* Logo */}
        <View style={styles.navbar}>
          <Image 
            source={require('../../assets/dark-logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>
        
        {/* Banner Section */}
        <View style={styles.bannerContainer}>
          <Text style={styles.bannerTitle}>أهلاً وسهلاً</Text>
          <Text style={styles.bannerSubtitle}>Ahlan w Sahlan</Text>
          <Text style={styles.bannerDescription}>Welcome to Kuwaiti Words</Text>
        </View>

        {/* Cards Grid */}
        <ScrollView 
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.cardsContainer}>
            <View style={styles.cardsGrid}>
              {cards.map((card) => (
                <TouchableOpacity
                  key={card.id}
                  style={styles.card}
                  activeOpacity={0.7}
                  onPress={() => handleCardPress(card.title, card.action)}
                >
                <View style={[styles.cardContent, { backgroundColor: card.backgroundColor }]}>
                  <View style={styles.iconContainer}>
                    <Ionicons name={card.icon} size={40} color={colors.white} />
                  </View>
                  <Text style={styles.cardTitle}>{card.title}</Text>
                  <Text style={styles.cardSubtitle}>{card.subtitle}</Text>
                </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity 
              onPress={() => Linking.openURL('https://www.instagram.com/kuwaitiwords')}
              style={styles.footerButton}
            >
              <Ionicons name="logo-instagram" size={20} color={colors.primary} />
              <Text style={styles.footerText}>@kuwaitiwords</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </SafeAreaView>
    </ImageBackground>
  );
};

const styles = StyleSheet.create({
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  container: {
    flex: 1,
  },
  navbar: {
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: Platform.OS === 'android' ? StatusBar.currentHeight : 0,
  },
  logo: {
    height: 300,
    width: '80%',
  },
  bannerContainer: {
    paddingVertical: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  bannerTitle: {
    fontSize: 42,
    fontWeight: 'bold',
    color: colors.primary,
    fontFamily: 'KhebratMusamim',
    marginBottom: 5,
  },
  bannerSubtitle: {
    fontSize: 28,
    color: colors.accent,
    fontFamily: 'KhebratMusamim',
    marginBottom: 5,
  },
  bannerDescription: {
    fontSize: 16,
    color: colors.gray,
    fontFamily: 'KhebratMusamim',
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  cardsContainer: {
    alignItems: 'center',
    width: '100%',
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    maxWidth: 400,
    width: '100%',
    paddingHorizontal: 20,
    marginTop: 10,
  },
  card: {
    width: '47%',
    aspectRatio: 1,
    marginBottom: 20,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 8,
  },
  cardContent: {
    flex: 1,
    borderRadius: 20,
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainer: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: colors.white,
    textAlign: 'center',
    fontFamily: 'KhebratMusamim',
    marginBottom: 5,
  },
  cardSubtitle: {
    fontSize: 12,
    color: colors.white,
    textAlign: 'center',
    fontFamily: 'KhebratMusamim',
    opacity: 0.85,
    fontStyle: 'italic',
  },
  footer: {
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 10,
  },
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  footerText: {
    marginLeft: 8,
    fontSize: 16,
    color: colors.primary,
    fontFamily: 'KhebratMusamim',
  },
});

export default LandingScreen;

